import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg, isDuplicate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Field, Select } from "./common";

const EMPTY = { client_name: "", studio_name: "", contact_person: "", phone: "", whatsapp: "", email: "", city: "", source: "Instagram",
  service: "Album Designing", est_sheets: 0, est_value: 0, assigned_to: "", next_followup: "", last_contact: "", priority: "Medium", notes: "", shared: false };

export function useDirectory(role) {
  const [list, setList] = useState([]);
  const { user } = useAuth();
  useEffect(() => {
    if (user.role === "owner") api.get("/users/directory").then((r) => setList(r.data.filter((u) => !role || u.role === role)));
  }, [user.role, role]);
  return list;
}

export default function LeadForm({ open, onClose, initial, onSaved }) {
  const { user, settings, meta } = useAuth();
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const sales = useDirectory("sales");
  const owners = useDirectory("owner");
  useEffect(() => { if (open) setF(initial ? { ...EMPTY, ...initial } : EMPTY); }, [open, initial]);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));

  const save = async (force = false) => {
    setBusy(true);
    const body = { ...f, email: f.email || null, next_followup: f.next_followup || null, last_contact: f.last_contact || null,
      assigned_to: f.assigned_to || null, est_sheets: Number(f.est_sheets || 0), est_value: Number(f.est_value || 0), force };
    try {
      const r = initial?.id ? await api.put(`/leads/${initial.id}`, body) : await api.post("/leads", body);
      toast.success(initial?.id ? "Lead updated" : `Lead ${r.data.code} created`);
      onSaved?.(r.data);
      onClose();
    } catch (e) {
      if (isDuplicate(e)) {
        const d = e.response.data.detail;
        if (window.confirm(`${d.message}: ${d.duplicates.map((x) => `${x.code} (${x.client_name})`).join(", ")}.\n\nCreate anyway?`)) return save(true);
      } else toast.error(errMsg(e));
    } finally { setBusy(false); }
  };

  const assignees = [...owners, ...sales].map((u) => ({ value: u.id, label: `${u.name} (${u.role})` }));
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="lead-form-dialog">
        <DialogHeader><DialogTitle>{initial?.id ? `Edit ${initial.code}` : "New Lead"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Client name *"><input data-testid="lead-client-name" className="vmw-input" value={f.client_name} onChange={set("client_name")} /></Field>
          <Field label="Studio name"><input data-testid="lead-studio-name" className="vmw-input" value={f.studio_name} onChange={set("studio_name")} /></Field>
          <Field label="Contact person"><input data-testid="lead-contact-person" className="vmw-input" value={f.contact_person} onChange={set("contact_person")} /></Field>
          <Field label="Phone *"><input data-testid="lead-phone" className="vmw-input" value={f.phone} onChange={set("phone")} /></Field>
          <Field label="WhatsApp"><input data-testid="lead-whatsapp" className="vmw-input" value={f.whatsapp} onChange={set("whatsapp")} /></Field>
          <Field label="Email"><input data-testid="lead-email" type="email" className="vmw-input" value={f.email || ""} onChange={set("email")} /></Field>
          <Field label="City"><input data-testid="lead-city" className="vmw-input" value={f.city} onChange={set("city")} /></Field>
          <Field label="Lead source"><Select testid="lead-source" options={settings?.lead_sources || []} value={f.source} onChange={set("source")} /></Field>
          <Field label="Service required"><Select testid="lead-service" options={settings?.services || []} value={f.service} onChange={set("service")} /></Field>
          <Field label="Priority"><Select testid="lead-priority" options={meta?.priorities || []} value={f.priority} onChange={set("priority")} /></Field>
          <Field label="Estimated sheets"><input data-testid="lead-est-sheets" type="number" min="0" className="vmw-input" value={f.est_sheets} onChange={set("est_sheets")} /></Field>
          <Field label="Estimated deal value (₹)"><input data-testid="lead-est-value" type="number" min="0" className="vmw-input" value={f.est_value} onChange={set("est_value")} /></Field>
          <Field label="Next follow-up"><input data-testid="lead-next-followup" type="date" className="vmw-input" value={f.next_followup || ""} onChange={set("next_followup")} /></Field>
          <Field label="Last contact"><input data-testid="lead-last-contact" type="date" className="vmw-input" value={f.last_contact || ""} onChange={set("last_contact")} /></Field>
          {user.role === "owner" && <Field label="Assigned salesperson"><Select testid="lead-assigned-to" placeholder="Unassigned" options={assignees} value={f.assigned_to} onChange={set("assigned_to")} /></Field>}
          {user.role === "owner" && (
            <label className="flex items-center gap-2 text-sm mt-6"><input data-testid="lead-shared" type="checkbox" checked={!!f.shared} onChange={(e) => setF({ ...f, shared: e.target.checked })} /> Shared with all sales executives</label>
          )}
          <Field label="Notes" className="sm:col-span-2"><textarea data-testid="lead-notes" className="vmw-input h-20 py-2" value={f.notes} onChange={set("notes")} /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="lead-form-cancel">Cancel</Button>
          <Button className="vmw-gradient" disabled={busy} onClick={() => save(false)} data-testid="lead-form-submit">{initial?.id ? "Save changes" : "Create lead"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
